/**
 * WebSocket message models for the Web Transcribe Streaming system
 */

import { AudioChunk } from './audio';
import { LanguageOption, SessionConfig } from './language';
import { TranscriptionResult } from './transcription';

export interface ErrorMessage {
  /** Error code for programmatic handling */
  code: string;
  /** Human-readable error message */
  message: string;
  /** Whether the error is recoverable */
  recoverable: boolean;
  /** Timestamp when the error occurred */
  timestamp: number;
  /** Additional technical details for debugging */
  details?: string;
}

export interface StatusUpdate {
  /** Current status of the transcription session */
  status: 'connecting' | 'connected' | 'transcribing' | 'disconnected' | 'error';
  /** Additional status information */
  message?: string;
  /** Timestamp of the status update */
  timestamp: number;
  /** Session ID if available */
  sessionId?: string;
}

/**
 * Configuration message sent when starting a transcription session
 * Requirements: 8.4, 8.5
 */
export interface ConfigPayload {
  /** Selected language for transcription */
  language: LanguageOption;
  /** Audio format (e.g., 'pcm') */
  audioFormat: string;
  /** Audio sample rate in Hz */
  sampleRate: number;
}

/**
 * Session control payload for start/stop session messages
 */
export interface SessionControlPayload {
  /** Optional session parameters */
  [key: string]: any;
}

export type MessagePayload = AudioChunk | TranscriptionResult | ErrorMessage | StatusUpdate | ConfigPayload | SessionControlPayload;

export interface WebSocketMessage {
  /** Type of the message */
  type: 'audio' | 'transcription' | 'error' | 'status' | 'config' | 'start_session' | 'stop_session';
  /** Message payload */
  payload: MessagePayload;
  /** Unique message identifier */
  messageId: string;
}

export interface AudioMessage extends WebSocketMessage {
  type: 'audio';
  payload: AudioChunk;
}

export interface TranscriptionMessage extends WebSocketMessage {
  type: 'transcription';
  payload: TranscriptionResult;
}

export interface ErrorWebSocketMessage extends WebSocketMessage {
  type: 'error';
  payload: ErrorMessage;
}

export interface StatusMessage extends WebSocketMessage {
  type: 'status';
  payload: StatusUpdate;
}

/**
 * Configuration message for starting a transcription session
 * Requirements: 8.4, 8.5
 */
export interface ConfigMessage extends WebSocketMessage {
  type: 'config';
  payload: ConfigPayload;
}

/**
 * Session control messages for starting/stopping transcription sessions
 */
export interface StartSessionMessage extends WebSocketMessage {
  type: 'start_session';
  payload: SessionControlPayload;
}

export interface StopSessionMessage extends WebSocketMessage {
  type: 'stop_session';
  payload: SessionControlPayload;
}

/**
 * Creates a config message for starting a transcription session
 * @param language - Selected language option
 * @param audioFormat - Audio format (default: 'pcm')
 * @param sampleRate - Sample rate in Hz (default: 16000)
 * @returns ConfigMessage ready to send via WebSocket
 */
export function createConfigMessage(
  language: LanguageOption,
  audioFormat: string = 'pcm',
  sampleRate: number = 16000
): ConfigMessage {
  return {
    type: 'config',
    payload: {
      language,
      audioFormat,
      sampleRate,
    },
    messageId: typeof crypto !== 'undefined' ? crypto.randomUUID() : Math.random().toString(36),
  };
}